import {
  Card,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tabs,
  TabsProps,
} from "@mui/material";
import * as React from "react";
import { useLanguage } from "./Language";
import { useLocationConfiguration } from "./LocationConfiguration";
import { VerticalLinkTab } from "./DatabaseTabs";
import { TLCEventManager } from "../events/EventManager";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { useSyndeticsConfig } from "./SyndeticsConfig";
import MARCDialog from "./MARCDialog";
import { ResourceDWR } from "../dto/SuggestionResult";
import axios from "axios";

interface HostSystemDiagImpl {
  methodSupported: boolean;
  validHostUser: boolean;
  validSessionUser: boolean;
  hostSystemFailure: boolean;
}
interface GetRealtimeHostRecordInformationReturn {
  hostSystemDiag: HostSystemDiagImpl;
  totalCheckouts: number;
  totalPendingRequests: number;
}

export interface DetailsTabsInterface extends TabsProps {
  resourceData: ResourceDWR;
  currentIndex: number;
}
export enum DetailsTabKey {
  fullDetailsDetailsTab = "fullDetailsDetailsTab",
  allCopiesDetailsTab = "allCopiesDetailsTab",
  reviewsDetailsTab = "reviewsDetailsTab",
  relatedDetailsTab = "relatedDetailsTab",
  lookInsideDetailsTab = "lookInsideDetailsTab",
}
export const DetailsTabs: React.FC<DetailsTabsInterface> = ({
  resourceData,
  currentIndex,
  ...props
}) => {
  const [totalCheckouts, setTotalCheckouts] = React.useState<number>(null);
  const [totalPendingRequests, setTotalPendingRequests] =
    React.useState<number>(null);
  const { locationConfiguration } = useLocationConfiguration();

  const showMARCRecordView =
    locationConfiguration?.pacSettings?.displaySettings?.showMarcRecordView;
  const showTotalCheckouts =
    locationConfiguration?.pacSettings?.displaySettings?.showTotalCheckouts;
  const showTotalHolds =
    locationConfiguration?.pacSettings?.displaySettings?.showTotalHolds;
  React.useEffect(() => {
    if (resourceData.rtype === 1) {
      axios
        .get<GetRealtimeHostRecordInformationReturn>(
          `/hostSystem/getRealtimeHostRecordInformation/${resourceData.hostBibliographicId}`
        )
        .then(({ data }) => {
          setTotalCheckouts(data.totalCheckouts);
          setTotalPendingRequests(data.totalPendingRequests);
        });
    }
  }, []);

  const { translate } = useLanguage();
  const {
    syndeticsConfig,
    hasMoreInfoTabEnrichments,
    hasRelatedTabEnrichments,
    hasReviewsTabEnrichments,
  } = useSyndeticsConfig();
  const { locationConfiguration: { pacSettings } = { pacSettings: null } } =
    useLocationConfiguration();
  const [view, setView] = React.useState<DetailsTabKey>(
    DetailsTabKey.fullDetailsDetailsTab
  );

  const baseUrl = `#section=resource&resourceid=${resourceData.id}&currentIndex=${currentIndex}`;

  React.useEffect(() => {
    const handleHashChange = () => {
      const urlSearchParams = new URLSearchParams(
        window.location.hash.slice(1)
      );
      const viewParam = urlSearchParams.get("view");
      if (Object.keys(DetailsTabKey).includes(viewParam)) {
        setView(viewParam as DetailsTabKey);
      }
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const handleTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: DetailsTabKey
  ) => {
    event.preventDefault();
    TLCEventManager.fireEvent(
      LS2PAC_CONSTANTS.Events.onShowFullDetailsSubView,
      {
        viewName: newValue,
      }
    );
  };

  return (
    <Stack direction="column" gap={1} margin={1}>
      <Paper sx={{ pt: 1, pb: 1 }}>
        {pacSettings && syndeticsConfig && (
          <Tabs
            value={view}
            onChange={handleTabChange}
            {...props}
            orientation="vertical"
            indicatorColor="secondary"
            textColor="secondary"
          >
            <VerticalLinkTab
              href={`${baseUrl}&view=${DetailsTabKey.fullDetailsDetailsTab}`}
              value={DetailsTabKey.fullDetailsDetailsTab}
              label={translate("details")}
            />
            <VerticalLinkTab
              href={`${baseUrl}&view=${DetailsTabKey.allCopiesDetailsTab}`}
              value={DetailsTabKey.allCopiesDetailsTab}
              label={translate("allCopies")}
            />
            {(pacSettings?.displaySettings?.showUserReviewsView ||
              hasReviewsTabEnrichments) && (
              <VerticalLinkTab
                href={`${baseUrl}&view=${DetailsTabKey.reviewsDetailsTab}`}
                value={DetailsTabKey.reviewsDetailsTab}
                label={translate("reviews")}
              />
            )}
            {(pacSettings?.noveListSettings?.useNoveList ||
              hasRelatedTabEnrichments) && (
              <VerticalLinkTab
                href={`${baseUrl}&view=${DetailsTabKey.relatedDetailsTab}`}
                value={DetailsTabKey.relatedDetailsTab}
                label={translate("related")}
              />
            )}
            {hasMoreInfoTabEnrichments && (
              <VerticalLinkTab
                href={`${baseUrl}&view=${DetailsTabKey.lookInsideDetailsTab}`}
                value={DetailsTabKey.lookInsideDetailsTab}
                label={translate("lookInside")}
              />
            )}
          </Tabs>
        )}
      </Paper>
      {resourceData.rtype === 1 &&
        (showTotalHolds || showTotalCheckouts || showMARCRecordView) && (
          <Card>
            <List dense={true}>
              {showTotalHolds && (
                <ListItem>
                  <ListItemText
                    primary={`${translate(
                      "totalRequests"
                    )}: ${totalPendingRequests}`}
                  />
                </ListItem>
              )}
              {showTotalCheckouts && (
                <ListItem>
                  <ListItemText
                    primary={`${translate(
                      "totalCheckouts"
                    )}: ${totalCheckouts}`}
                    secondary={translate("includingRenewals")}
                  />
                </ListItem>
              )}
              {showMARCRecordView && (
                <ListItem>
                  <MARCDialog resourceId={resourceData.id}></MARCDialog>
                </ListItem>
              )}
            </List>
          </Card>
        )}
    </Stack>
  );
};
